import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import fileDownload from "js-file-download";
import _ from "lodash";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { RAW_DATA_HEADER } from "./headers";

dayjs.extend(utc);

export const handleDownloadChart = (clickedButton) => {
  const element = document.getElementById("chart-container");
  if (!element) {
    console.error("Element not found.");
    return;
  }

  html2canvas(element, {
    width: element.offsetWidth,
    height: element.offsetHeight,
    useCORS: true,
    allowTaint: true,
    scale: window.devicePixelRatio,
  }).then((canvas) => {
    const imageDataURL = canvas.toDataURL("image/jpeg", 1.0);
    const link = document.createElement("a");
    link.href = imageDataURL;
    link.download = `${clickedButton}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  });
};

const WEEKLY_FIXED_COLUMNS = ["date", "trapSite", "sum", "weeklyChange"];

export const reorderColumnsWithFixedEnd = (
  rows,
  { fixedStart, fixedEnd, sortMiddle = true, fallback = null, fallbackMode = "nullish" }
) => {
  const allKeys = Object.keys(rows[0] || {});
  const startColumn = fixedStart && allKeys.includes(fixedStart) ? [fixedStart] : [];
  const endColumns = fixedEnd.filter((key) => allKeys.includes(key));
  const excluded = [...startColumn, ...fixedEnd];

  let middleColumns = allKeys.filter((key) => !excluded.includes(key));
  if (sortMiddle) {
    middleColumns = middleColumns.sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }

  const columnOrder = [...startColumn, ...middleColumns, ...endColumns];

  const reorderedRows = rows.map((item) => {
    const reorderedItem = {};
    columnOrder.forEach((col) => {
      reorderedItem[col] =
        fallbackMode === "falsy"
          ? item[col] || fallback
          : item[col] !== undefined
            ? item[col]
            : fallback;
    });
    return reorderedItem;
  });

  return { columnOrder, rows: reorderedRows };
};

export const handleDownloadReport = (
  data,
  clickedButton,
  columnVisibilityModel
) => {
  const parsedData = JSON.parse(JSON.stringify(data));
  const title = clickedButton.replace(/-/g, " ");
  const downloadColumns = columnVisibilityModel;
  const type =
    clickedButton === "Raw-Data"
      ? "raw"
      : clickedButton === "Weekly-Species-report"
        ? "weekly"
        : "Species-Summary-Among-Collections";

  if (parsedData?.length > 0) {
    let modifiedData = [...parsedData];
    let columns = [];
    let newHeaderForRaw = [];
    if (type === "raw") {
      RAW_DATA_HEADER.forEach((value) => {
        if (downloadColumns[value["field"]]) {
          columns.push(value["field"]);
          newHeaderForRaw.push(value["headerName"]);
        }
      });
      modifiedData = modifiedData.map((item) => {
        const updatedItem = _.pick({ ...item }, columns);
        Object.keys(updatedItem).forEach((key) => {
          if (
            key.toLowerCase().includes("genus") &&
            typeof updatedItem[key] === "string"
          ) {
            updatedItem[key] = _.capitalize(updatedItem[key]);
          }
          if (key === "specimen_capture_date" && updatedItem[key]) {
            updatedItem[key] = dayjs.utc(updatedItem[key]).format("M/D/YYYY");
          }
        });
        return updatedItem;
      });
    }

    if (type === "weekly") {
      modifiedData = _.orderBy(
        data,
        [
          "date",
          (item) => (item.trapSite === "undefined" ? 1 : 0),
          (item) => (item.trapSite || "").toLowerCase(),
        ],
        ["asc", "asc", "asc"]
      );

      // Species columns arrive in whatever order the API returned them, so
      // alphabetize them behind the fixed leading columns.
      const allKeys = Object.keys(modifiedData[0] || {});
      const speciesColumns = allKeys
        .filter((key) => !WEEKLY_FIXED_COLUMNS.includes(key) && key !== "id")
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
      columns = [
        ...WEEKLY_FIXED_COLUMNS.filter((key) => allKeys.includes(key)),
        ...speciesColumns,
      ];
    }

    if (type === "Species-Summary-Among-Collections") {
      // The rows come from the data grid, which numbers them with an `id` it
      // needs and the report does not.
      modifiedData = modifiedData.map(({ id, ...row }) => row);
      modifiedData.sort((a, b) => {
        if (a.species === "All") return -1;
        if (b.species === "All") return 1;
        return (a.species || "").localeCompare(b.species || "", undefined, {
          sensitivity: "base",
        });
      });

      const reordered = reorderColumnsWithFixedEnd(modifiedData, {
        fixedStart: "species",
        fixedEnd: ["Male", "Female", "Sex unknown", "Total_Count"],
        sortMiddle: true,
        fallback: 0,
        fallbackMode: "nullish",
      });
      columns = reordered.columnOrder;
      modifiedData = reordered.rows;
    }

    const worksheet = XLSX.utils.json_to_sheet(modifiedData, {
      header: columns,
    });
    if (newHeaderForRaw.length > 0) {
      XLSX.utils.sheet_add_aoa(worksheet, [newHeaderForRaw], { origin: "A1" });
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data Sheet");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    fileDownload(
      blob,
      `${title}-Report-${new Date().toISOString().substring(0, 10)}.xlsx`
    );
  }
};
